import { use, useRef, useState } from "react";
import { useDbClient, useLiveQuery } from "@tanstack/react-db";
import {
  collectionError,
  collectionRefreshing,
  QueryViewerContext,
} from "@softmax/web-features/data";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import { myPlayersCollection } from "@/features/players/records";
import type { CredentialIntent, CredentialOpening } from "./credential-intent";
import type { PlayerIntent } from "./player-operation";

export function usePlayerCredentials() {
  const viewerId = use(QueryViewerContext);
  const descriptor = myPlayersCollection({
    viewerId,
    elevatedPrivileges: false,
  });
  const source = useDbClient().collection(descriptor);
  const query = useLiveQuery((q) => q.from({ player: descriptor }));
  const error = collectionError(query.health);
  const unavailable = requestUnavailable(error);
  const [refreshing, setRefreshing] = useState(false);
  const refreshPending = useRef(false);
  const busy = refreshing || collectionRefreshing(query.health);
  const [credentialIntent, setCredentialIntent] =
    useState<CredentialIntent | null>(null);
  const [playerIntent, setPlayerIntent] = useState<PlayerIntent | null>(null);
  const finalFocusRef = useRef<HTMLElement | null>(null);

  async function refreshPlayers() {
    if (refreshPending.current) return false;

    refreshPending.current = true;
    setRefreshing(true);
    const read = query.isLoadingError
      ? query.collection.reload()
      : source.utils.refetch({ throwOnError: true });
    return read
      .then(
        () => source.status === "ready",
        () => false,
      )
      .finally(() => {
        refreshPending.current = false;
        setRefreshing(false);
      });
  }

  if (unavailable && (credentialIntent !== null || playerIntent !== null)) {
    setCredentialIntent(null);
    setPlayerIntent(null);
  }

  const openCredential = ({ intent, invoker }: CredentialOpening) => {
    finalFocusRef.current =
      intent.action === "create" ? intent.successFocusRef.current : invoker;
    setCredentialIntent(intent);
  };
  const openPlayer = async (intent: PlayerIntent) => {
    if (busy || query.isLoading || unavailable) return;

    const activeElement = document.activeElement;
    const invoker = activeElement instanceof HTMLElement ? activeElement : null;

    if (!(await refreshPlayers())) return;

    if (intent.kind === "rename") {
      const playerId = intent.player.id;
      const player = source.get(playerId);

      if (!player) return;

      intent = { kind: "rename", player };
    }

    finalFocusRef.current = invoker;
    setPlayerIntent(intent);
  };
  return {
    query,
    error,
    unavailable,
    busy,
    credentialIntent,
    setCredentialIntent,
    playerIntent,
    setPlayerIntent,
    finalFocusRef,
    refreshPlayers,
    openCredential,
    openPlayer,
  };
}
